/**
 * What a planner save names as removed, and the server ids it learned.
 *
 * The planner autosaves its whole itinerary. The server used to read
 * "missing from this save" as "removed", so when two people edited one trip,
 * the second save deleted whatever the first had added since the second
 * planner loaded (Jenny's waterpark, 2026-10-16). Now each save names its
 * removals: everything the planner last loaded or saved (the baseline) that
 * is no longer in its itinerary. Anything the server holds beyond that is
 * someone else's, and stays. Server side: tripsService.saveActivitiesToTrip,
 * "Deletes are named".
 *
 * A stop added this session carries an id the planner minted for it
 * (`clientActivityId`, in the server's own `act_<ms>_<hex32>` format,
 * `withClientIds`). Its first save stores the row under that id, so any later
 * save — even one sent before the first answered — names the same row. Its
 * display `id` is NOT that: add paths set it to the place id, so two stops
 * of one place share it, and it can't key anything. When the server instead
 * matched an existing row by content (`inserted_activities` maps the minted
 * id to that row), `idMap` remembers it.
 */

const CLIENT_ACTIVITY_ID_RE = /^act_\d{13}_[0-9a-f]{32}$/;

function randomHex32() {
  const bytes = new Uint8Array(16);
  const c = typeof window !== 'undefined' ? window.crypto : null;
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

/** A new activity id in the server's format (server/utils/generateId.js). */
export function mintActivityId() {
  return `act_${Date.now()}_${randomHex32()}`;
}

/**
 * Days in which every stop the server hasn't stored yet carries its own
 * minted id: missing ones are minted, and a second stop holding the same one
 * (a stop copied by spreading another) gets a fresh one. Returns the input
 * when nothing needed one, so the mutation funnel keeps its no-op identity.
 */
export function withClientIds(days) {
  if (!Array.isArray(days)) return days;
  const seen = new Set();
  let changed = false;
  const next = days.map(day => {
    const activities = day?.activities;
    if (!Array.isArray(activities) || activities.length === 0) return day;
    let dayChanged = false;
    const mapped = activities.map(a => {
      if (!a || a.activityId || a.activity_id) return a;
      const own = a.clientActivityId;
      if (typeof own === 'string' && CLIENT_ACTIVITY_ID_RE.test(own) && !seen.has(own)) {
        seen.add(own);
        return a;
      }
      const minted = mintActivityId();
      seen.add(minted);
      dayChanged = true;
      return { ...a, clientActivityId: minted };
    });
    if (!dayChanged) return day;
    changed = true;
    return { ...day, activities: mapped };
  });
  return changed ? next : days;
}

/**
 * The server id of a stop: its own, or the row a save matched it to, or the
 * id it minted once a save has stored it (`idMap` holds those answers).
 * Null when no save has answered for it yet.
 */
export function serverActivityId(activity, idMap) {
  if (!activity) return null;
  const own = activity.activityId || activity.activity_id;
  if (own) return own;
  if (activity.clientActivityId && idMap && idMap.has(activity.clientActivityId)) {
    return idMap.get(activity.clientActivityId);
  }
  return null;
}

/**
 * Every id a stop may be stored under: its server id, else its minted id
 * (a save may have stored it without the answer arriving — naming an id the
 * server never stored is harmless).
 */
function nameableId(activity, idMap) {
  return serverActivityId(activity, idMap) || activity?.clientActivityId || null;
}

function activityIdSet(days, idMap) {
  const ids = new Set();
  (days || []).forEach(day => (day?.activities || []).forEach(a => {
    const id = nameableId(a, idMap);
    if (id) ids.add(id);
  }));
  return ids;
}

const dayKey = (day) => (day && day.date ? `${day.date}:${day.plan_variant || 'A'}` : null);

function dayKeySet(days) {
  return new Set((days || []).map(dayKey).filter(Boolean));
}

function accommodationUidSet(days) {
  const uids = new Set();
  (days || []).forEach(day => {
    const uid = day?.accommodation?.clientUid;
    if (uid) uids.add(uid);
  });
  return uids;
}

const minus = (a, b) => [...a].filter(x => !b.has(x));

/**
 * The removals one save names: baseline minus current, per kind. Ids only —
 * a stop with no server id yet was never saved, so there is nothing to name.
 */
export function namedRemovals({ baselineDays, currentDays, baselineTransports, currentTransports, idMap, extraActivityIds = null }) {
  const transportUids = (list) => new Set((list || []).map(t => t?.clientUid).filter(Boolean));
  // `extraActivityIds`: stops shown but not in the baseline — ones a re-read
  // merged into unsaved edits, and new stops sent in a save whose answer
  // never came (stored, maybe). Removing one is a removal.
  const shown = activityIdSet(baselineDays, idMap);
  (extraActivityIds || []).forEach(id => id && shown.add(id));
  return {
    removed_activity_ids: minus(shown, activityIdSet(currentDays, idMap)),
    // Days by date + plan variant, as the server keys them: a day another
    // member added (unseen here) is not named, so it stays.
    removed_day_keys: minus(dayKeySet(baselineDays), dayKeySet(currentDays)),
    removed_accommodation_uids: minus(accommodationUidSet(baselineDays), accommodationUidSet(currentDays)),
    removed_transport_uids: minus(transportUids(baselineTransports), transportUids(currentTransports))
  };
}

/**
 * Days whose stops carry the server ids `idMap` learned. Returns the input
 * when nothing needed one, so the payload costs nothing on a steady trip.
 */
export function withServerIds(days, idMap) {
  if (!idMap || idMap.size === 0) return days;
  let changed = false;
  const next = (days || []).map(day => {
    let dayChanged = false;
    const activities = (day?.activities || []).map(a => {
      if (!a || a.activityId || a.activity_id) return a;
      // A stop stored under its own minted id gets it too (CC-18-02): the
      // server reads a minted id for two days only, so after that a stop
      // that sends only its clientActivityId would be inserted again.
      const id = serverActivityId(a, idMap);
      if (!id) return a;
      dayChanged = true;
      return { ...a, activityId: id };
    });
    if (!dayChanged) return day;
    changed = true;
    return { ...day, activities };
  });
  return changed ? next : days;
}

/** Remember the ids a save gave the stops it created. */
export function learnInsertedIds(idMap, result) {
  (result?.inserted_activities || []).forEach(r => {
    if (r && r.client_id != null && r.activity_id) idMap.set(String(r.client_id), r.activity_id);
  });
}

/**
 * Whether a save's answer says the server holds something this planner
 * doesn't show (kept), or dropped something it still shows (gone): its cue
 * to re-read the trip. `seen` holds the ids already acted on, so a row the
 * re-read can't bring in (one with no day, say) asks once, not every save.
 */
export function saveNeedsReload(result, seen) {
  const fresh = (list) => (list || []).filter(id => id && !seen.has(id));
  const kept = fresh(result?.kept_activity_ids);
  const gone = fresh(result?.gone_activity_ids);
  const keptOther = fresh(result?.kept_transport_uids)
    .concat(fresh(result?.kept_accommodation_uids))
    .concat(fresh((result?.kept_day_keys || []).map(k => `day:${k}`)));
  [...kept, ...gone, ...keptOther].forEach(id => seen.add(id));
  return { reload: kept.length + gone.length + keptOther.length > 0, keptActivityIds: kept, goneActivityIds: gone };
}

/** Days without the stops whose ids are in `ids` (another member deleted them). */
export function dropActivities(days, ids, idMap) {
  const drop = new Set((ids || []).filter(Boolean));
  if (drop.size === 0) return days;
  let changed = false;
  const next = (days || []).map(day => {
    const activities = day?.activities || [];
    const kept = activities.filter(a => !drop.has(nameableId(a, idMap)));
    if (kept.length === activities.length) return day;
    changed = true;
    return { ...day, activities: kept };
  });
  return changed ? next : days;
}

/**
 * A re-read's days, with each stop this planner added keeping the display
 * `id` it has here (the server's copy is keyed by its activity_id). An open
 * edit sheet holds that id — swapping it under the sheet made its save find
 * nothing and fall back to a stale index (ItineraryTab's edit modal).
 * Returns the input when no stop needed it.
 */
export function keepLocalIds(loadedDays, localDays, idMap) {
  const localIdByServerId = new Map();
  // Only a display id one stop holds: add paths give a stop the place id, so
  // two stops of one place share it, and keeping it would make an edit to
  // the second land on the first (ops find a stop by id).
  const idCount = new Map();
  (localDays || []).forEach(day => (day?.activities || []).forEach(a => {
    if (a && a.id != null) idCount.set(a.id, (idCount.get(a.id) || 0) + 1);
  }));
  (localDays || []).forEach(day => (day?.activities || []).forEach(a => {
    if (a && idCount.get(a.id) > 1) return;
    // Every stop whose display id isn't its server id — stops added this
    // session, and ones an earlier re-read already gave their local id.
    if (!a || a.id == null) return;
    const id = nameableId(a, idMap);
    if (id && id !== a.id) localIdByServerId.set(id, a.id);
  }));
  if (localIdByServerId.size === 0) return loadedDays;
  let changed = false;
  const next = (loadedDays || []).map(day => {
    let dayChanged = false;
    const activities = (day?.activities || []).map(a => {
      const localId = a && localIdByServerId.get(a.activityId || a.activity_id);
      if (localId == null || localId === a.id) return a;
      dayChanged = true;
      return { ...a, id: localId };
    });
    if (!dayChanged) return day;
    changed = true;
    return { ...day, activities };
  });
  return changed ? next : loadedDays;
}

/**
 * Record the minted ids of new stops a save is about to carry, and return
 * every id recorded so far. A save may store a stop and lose its answer;
 * until a re-read the planner can't know, so removing such a stop names it
 * anyway (the server ignores an id it never stored).
 */
export function noteSentIds(days, sent) {
  (days || []).forEach(day => (day?.activities || []).forEach(a => {
    if (a && !a.activityId && !a.activity_id && a.clientActivityId) sent.add(a.clientActivityId);
  }));
  return [...sent];
}
